import { useEffect, useState } from 'react';
import { Box, Tabs, Tab, Typography, TextField, Button, IconButton, Alert, Chip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { getAfaSettings, saveAfaSettings } from '../../api/generalSettingsApi';
import WhatsappSettings from './whatsappSettings';
import TierSettings from '../admin/tierSettings';
import PermissionManager from './permissionManager';
import ApproverConfig from './approverConfig';
import ImportSales from './importSales';
import VersionInfo from './versionInfo';
import MaintenanceTab from './maintenanceTab';

const PP = "'Poppins', sans-serif";

// AFA (Automatic Fuel Adjustment) rates — month-dated, RSC auto-uses the current month.
function AfaTab() {
    const [rows, setRows] = useState([]);
    const [current, setCurrent] = useState(0);
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState(null);

    const ym = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

    useEffect(() => {
        (async () => {
            try {
                const res = await getAfaSettings();
                let rates = (res.data?.rates || []).map(r => ({ month: r.month, rate: r.rate }));
                // Pad to at least 3 rows with the upcoming months for convenience.
                const now = new Date();
                while (rates.length < 3) {
                    const d = new Date(now.getFullYear(), now.getMonth() + rates.length, 1);
                    if (!rates.some(r => r.month === ym(d))) rates.push({ month: ym(d), rate: 0 });
                    else rates.push({ month: '', rate: 0 });
                }
                setRows(rates);
                setCurrent(Number(res.data?.currentRate) || 0);
            } catch { setRows([{ month: ym(new Date()), rate: 0 }]); }
        })();
    }, []);

    const setRow = (i, key, val) => setRows(rs => rs.map((r, idx) => idx === i ? { ...r, [key]: val } : r));
    const addRow = () => setRows(rs => [...rs, { month: '', rate: 0 }]);
    const removeRow = (i) => setRows(rs => rs.filter((_, idx) => idx !== i));

    const save = async () => {
        setSaving(true); setMsg(null);
        try {
            const payload = { rates: rows.filter(r => r.month).map(r => ({ month: r.month, rate: Number(r.rate) || 0 })) };
            const res = await saveAfaSettings(payload);
            setCurrent(Number(res.data?.currentRate) || 0);
            setMsg({ type: 'success', text: 'AFA rates saved. The RSC calculator now uses the current month’s rate.' });
        } catch (e) {
            setMsg({ type: 'error', text: 'Save failed: ' + (typeof e.response?.data === 'string' ? e.response.data : e.message) });
        } finally { setSaving(false); }
    };

    return (
        <Box sx={{ maxWidth: 640 }}>
            <Typography sx={{ fontFamily: PP, fontSize: '0.85rem', color: '#475569', mb: 1.5 }}>
                TNB Automatic Fuel Adjustment (AFA) — a per-kWh surcharge that changes monthly.
                Enter the gazetted rate (sen/kWh) per month; the RSC bill calculator automatically
                uses whichever month is current. AFA is exempt for usage of 600 kWh and below.
            </Typography>
            <Chip label={`Current month rate in use: ${current} sen/kWh`} sx={{ mb: 2, fontFamily: PP, fontWeight: 600, bgcolor: '#EFF6FF', color: '#0F62CC' }} />

            {rows.map((r, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 1.5 }}>
                    <TextField
                        label="Month" type="month" size="small" InputLabelProps={{ shrink: true }}
                        value={r.month} onChange={e => setRow(i, 'month', e.target.value)} sx={{ width: 180 }}
                    />
                    <TextField
                        label="Rate (sen/kWh)" type="number" size="small"
                        value={r.rate} onChange={e => setRow(i, 'rate', e.target.value)} sx={{ width: 170 }}
                        inputProps={{ step: '0.01' }}
                    />
                    <IconButton size="small" onClick={() => removeRow(i)}><DeleteIcon sx={{ fontSize: 18, color: '#EF4444' }} /></IconButton>
                </Box>
            ))}

            <Button size="small" startIcon={<AddIcon />} onClick={addRow} sx={{ textTransform: 'none', fontFamily: PP, mb: 2 }}>
                Add month
            </Button>

            {msg && <Alert severity={msg.type} sx={{ mb: 2, fontFamily: PP, fontSize: '0.82rem' }}>{msg.text}</Alert>}

            <Box>
                <Button variant="contained" onClick={save} disabled={saving}
                    sx={{ fontFamily: PP, fontWeight: 600, textTransform: 'none', borderRadius: '8px', background: 'linear-gradient(135deg, #1A8CFF, #0F62CC)', boxShadow: 'none' }}>
                    {saving ? 'Saving...' : 'Save AFA Rates'}
                </Button>
            </Box>
        </Box>
    );
}

export default function GeneralSettings() {
    const [tab, setTab] = useState(0);
    return (
        <Box>
            <Box sx={{ mb: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, fontFamily: PP, color: '#0F172A' }}>General Settings</Typography>
                <Typography sx={{ fontSize: '0.82rem', color: '#64748B', fontFamily: PP }}>System-wide configuration (Super Admin)</Typography>
            </Box>
            <Box sx={{ backgroundColor: '#fff', borderRadius: '14px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
                <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{
                    borderBottom: '1px solid #E2E8F0', px: 1,
                    '& .MuiTab-root': { fontFamily: PP, textTransform: 'none', fontWeight: 600, fontSize: '0.85rem' },
                    '& .Mui-selected': { color: '#1A8CFF' },
                    '& .MuiTabs-indicator': { backgroundColor: '#1A8CFF' },
                }}>
                    <Tab label="AFA Tariff" />
                    <Tab label="WhatsApp" />
                    <Tab label="Tier" />
                    <Tab label="Permission Manager" />
                    <Tab label="Approver Config" />
                    <Tab label="Import Sales" />
                    <Tab label="Version Info" />
                    <Tab label="Maintenance" />
                </Tabs>
                <Box sx={{ p: { xs: 2, md: 3 } }}>
                    {tab === 0 && <AfaTab />}
                    {tab === 1 && <WhatsappSettings />}
                    {tab === 2 && <TierSettings />}
                    {tab === 3 && <PermissionManager />}
                    {tab === 4 && <ApproverConfig />}
                    {tab === 5 && <ImportSales />}
                    {tab === 6 && <VersionInfo />}
                    {tab === 7 && <MaintenanceTab />}
                </Box>
            </Box>
        </Box>
    );
}
